import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, Button, CardView, Spacer, TextButton } from 'design-system';
import BottomPanel from 'design-system/BottomPanel';
import { HEIGHT } from 'design-system/Button';
import CardRow from 'design-system/CardRow';
import CardRowContainer from 'design-system/CardRowContainer';
import CardUpgradeRow from 'design-system/CardUpgradeRow';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { IconRocket } from 'design-system/icons/IconRocket';
import IconTooltip from 'design-system/icons/IconTooltip';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Squircle from 'design-system/Squircle';
import Text from 'design-system/Text';
import { hitSlopS, rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useAppRating from 'hooks/useAppRating';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { createSavingsPlan, editSavingsPlan } from 'features/pots/actions';
import AutosaveFeeInfoModal from 'features/pots/components/AutosaveFeeInfoModal';
import { selectAutosaveFees } from 'features/pots/reducers/selectors';
import { SavingsPlanRuleType } from 'features/pots/types';
import { getPeriodFromRRule } from 'features/pots/utils';
import { getConnectedAccount } from 'actions/invest';
import { formatAmount } from 'utils';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
import Links, { knownLinks } from 'utils/links';
import { goBackToTopWithStops } from 'utils/navigationv6';
const fetchConfig = {
    throws: true,
    muteError: true,
    muteResult: true,
};
const AutosaveReviewInfo = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const rateApp = useAppRating();
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>,
        });
    }, []);
    const autosaveId = useAppSelector((state) => state.pots.savingsPlan?.id);
    const savingsPlanFrequency = useAppSelector((state) => state.pots.savingsPlanFrequency);
    const fees = useAppSelector(selectAutosaveFees);
    const [, , requesting, fetch] = useFetchOld(fetchConfig);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle);
    const savingRules = useMemo(() => {
        const rulesObj = route.params.rules.reduce((prev, curr) => {
            if (curr.type === SavingsPlanRuleType.AUTOMATED) {
                return {
                    ...prev,
                    automated: true,
                };
            }
            if (curr.type === SavingsPlanRuleType.CUSTOM) {
                return {
                    ...prev,
                    custom: true,
                };
            }
            if (curr.type === SavingsPlanRuleType.ROUND_UP) {
                return {
                    ...prev,
                    roundup: true,
                };
            }
            return prev;
        }, {});
        let finalStr = '';
        let comma = false;
        if (rulesObj?.automated) {
            finalStr += 'AI';
            comma = true;
        }
        if (rulesObj?.custom) {
            finalStr += comma ? ', Custom' : 'Custom';
            comma = true;
        }
        if (rulesObj?.roundup) {
            finalStr += comma ? ', Round Ups' : 'Round Ups';
        }
        return finalStr;
    }, [route.params.rules]);
    const handleLearnMore = useCallback(() => {
        if (Platform.OS === 'android') {
            navigation.navigateDeprecated('Pdf', { url: knownLinks.potsTnc, buttonTitle: 'Done' });
        }
        else {
            Links.openPotsTnc();
        }
    }, []);
    const showFeeModal = useCallback(() => {
        if (typeof fees === 'number') {
            AutosaveFeeInfoModal.show({ value: fees, currency: 'GBP' });
        }
    }, [fees]);
    const handleContinue = useCallback(async () => {
        try {
            if (autosaveId && route.params.bankAccount) {
                await fetch(editSavingsPlan(autosaveId, {
                    rules: route.params.rules,
                    accountId: route.params.bankAccount.id,
                    allowOverdraft: route.params.overdraft,
                    distribution: route.params.distribution,
                }));
            }
            else if (route.params.bankAccount &&
                route.params.distribution &&
                typeof route.params.overdraft === 'boolean') {
                await fetch(createSavingsPlan(route.params.bankAccount, route.params.overdraft, route.params.distribution, route.params.rules, route.params.collectionFrequency));
                Amplitude.logEvent('Submitted Request', {
                    type: 'autosave',
                });
                dispatch(getConnectedAccount());
            }
            navigation.navigateDeprecated('Success', {
                title: 'Autosave has been enabled',
                subTitle: 'Your Direct Debit has been setup. Emma will start saving for you automatically. You can pause this at any time.',
                buttonText: 'Done',
                onContinue: () => {
                    goBackToTopWithStops(navigation, ['Report', 'Pot']);
                    return true;
                },
            });
            rateApp();
        }
        catch {
            // no-op
        }
    }, [
        autosaveId,
        route.params.bankAccount,
        route.params.distribution,
        route.params.overdraft,
        route.params.rules,
        route.params.collectionFrequency,
        rateApp,
    ]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Review and confirm"/>
          <CardView noMarginTop>
            <View style={styles.cardHeader}>
              <View style={styles.titleContainer}>
                <Text Text-16>Autosave</Text>
              </View>
              <Squircle width={rem(44)} height={rem(44)} color={colors.background.dark}>
                <IconRocket colorKey="brand"/>
              </Squircle>
            </View>
            <CardRowContainer>
              <CardRow title="Saving rules" info={savingRules} testID="savingModeRow" small smallPadding/>
              <CardRow title="Frequency" testID="frequencyRow" small smallPadding info={getPeriodFromRRule(route.params.collectionFrequency.rrule) === 'Monthly'
            ? 'Every month'
            : savingsPlanFrequency}/>
              {fees !== undefined && (<CardRow title={<View style={styles.row}>
                      <Text TextThin-14 Secondary>
                        Autosave fee
                      </Text>
                      <Spacer.W8 />
                      <View>
                        <Spacer h={4}/>
                        <TouchableOpacity hitSlop={hitSlopS} onPress={showFeeModal}>
                          <IconTooltip height={16} width={16} colorKey="secondary"/>
                        </TouchableOpacity>
                      </View>
                    </View>} smallPadding info={<Text Numbers-14>{formatAmount(fees, 'GBP', 2)}</Text>} testID="transactionFeeRow"/>)}
            </CardRowContainer>
            {!!fees && (<>
                <CardUpgradeRow title="Want no fee?" withHorizontalMargins/>
                <Spacer.H16 />
              </>)}
          </CardView>
        </Animated.ScrollView>
        <BottomPanel>
          <View style={styles.textBtnWrapper}>
            <TextButton small onPress={handleLearnMore} text={<Text TextThin-16 Secondary>
                  T&Cs apply
                </Text>}/>
          </View>
          <Button brand loading={requesting} title="Confirm" onPress={handleContinue}/>
        </BottomPanel>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets(() => ({
    cardHeader: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    titleContainer: {
        flex: 1,
    },
    textBtnWrapper: {
        alignItems: 'center',
        marginBottom: rem(16),
    },
    row: {
        flex: 1,
        flexDirection: 'row',
    },
}));
export default AutosaveReviewInfo;
